import { countedKey } from "@/lib/i18n/plural";
import { canUseBackOffice } from "@/modules/auth/backOffice";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Button, EmptyState, ErrorState, LoadingSkeleton } from "@a2z/ui";
import { useI18n, useTranslate } from "@/lib/i18n";
import { useErrorMessage } from "@/shared/errors";
import { usePrivateToast } from "@/shared/usePrivateToast";
import { Screen } from "@/shared/ui";
import {
  PERMISSIONS,
  hasPermission,
  useSession,
} from "@/modules/auth/useSession";
import { useLogout } from "@/modules/auth/useLogout";

import type { KitchenStation } from "@/lib/api/types";
import { useKitchenBoard } from "../lib/useKitchenBoard";
import { KitchenLanes } from "../components/KitchenLanes";
import { KitchenOrderView } from "../components/KitchenOrderView";
import { KitchenBatchStrip } from "../components/KitchenBatchStrip";
import { KitchenRecallDrawer } from "../components/KitchenRecallDrawer";
import { boardWorkflow, hasBoard } from "../lib/workflow";
import { readyAgeOffMinutesOf, splitAgedOff, splitAgedOffByOrder, visibleRetained } from "../lib/ageOff";
import { struckVoids } from "../lib/voids";
import { isOffPremise } from "../lib/channel";
import { useKitchenViewPreference, type KitchenView } from "../lib/kitchenViewPreference";
import {
  KITCHEN_HIDDEN_PAID_KEY,
  KITCHEN_PINNED_READY_KEY,
  useKitchenDeviceMarks,
} from "../lib/kitchenDeviceMarks";
import { useNewTicketMark } from "../lib/useNewTicketMark";
import { useApplyDashboardTheme } from "@/app/themes/dashboardTheme";
import { usePhoneWidth } from "@/shared/hooks/usePhoneWidth";
import { useKitchenSize } from "../lib/kitchenSizePreference";
import { useOptionalRestaurantNotifications } from "@/modules/notifications/NotificationsProvider";
import { ticketChimeKey, useNewTicketChime } from "../lib/useNewTicketChime";
import { useScreenWakeLock } from "../lib/useScreenWakeLock";
import { KitchenStartOverlay } from "../components/KitchenStartOverlay";
import { KitchenOfflineBand } from "../components/KitchenOfflineBand";
import { KitchenSignOutSheet } from "../components/KitchenSignOutSheet";
import { KitchenScreenSheet } from "../components/KitchenScreenSheet";
import { ticketKeyOf } from "../components/PaperKitchen";

/**
 * The width at which the three lanes sit side by side — Tailwind's `lg`.
 *
 * The lane cap below is scoped to it deliberately. At `lg` and up this is a
 * WALL: three columns, one screenful, nothing below a fold because nobody with
 * a pan in their hand is going to scroll a page. Below it the lanes stack, and
 * a stacked board on a phone in a pocket is an ordinary scrolling list — giving
 * each of three stacked lanes its own little scroll box there would produce
 * three tiny windows on one small screen, which is worse than the page scroll.
 */
const LANES_SIDE_BY_SIDE = "(min-width: 1024px)";

/**
 * `.app-main`'s own bottom padding (`p-4`), which the cap must leave alone —
 * the fallback only: the cap reads the padding the look actually gave `main`
 * (PAPER's pane is 24px 28px from 1024px up, `_paper-till.css`).
 */
const BOARD_BOTTOM_GUTTER_PX = 16;

/**
 * Below this, stop capping and let the page scroll.
 *
 * A short viewport (a laptop with every browser bar open, or a rotated tablet)
 * can leave so little room under the station chips that a capped lane would
 * show no whole card at all — a board showing nothing but "+5 below". The page
 * scroll is the worse answer in general and the better one here.
 */
const MIN_LANE_HEIGHT_PX = 260;

/**
 * The kitchen board (KDS).
 *
 * ─── WHERE THIS SCREEN IS READ ───────────────────────────────────────────────
 * Not in a hand, on a wall: hot, loud, busy, and looked at from two or three
 * steps away by someone whose hands are full. Status is stated in words as well
 * as tone so it survives both greyscale and a glance. The sizes are set by the
 * DEVICE (`data-kitchen-size`, `lib/kitchenSizePreference.ts`; CSS in
 * `app/themes/_paper-kitchen.css`, P5):
 *   - desk (a mouse, `auto`): the design's own sizes;
 *   - touch: every control ≥ 44px tall; dish 20px, details and meta 15px,
 *     actions 16px — nothing under 15px on a card;
 *   - wall (measured at 1920×1080): dish 40px, details ≥ 26px/700, footer
 *     actions ≥ 64px tall, line actions and station chips ≥ 56px.
 * The board prints nothing: a reprint is the till's (D129).
 *
 * It stays on the LIGHT platform palette rather than the till's `.pos-dark`
 * scope. `docs/design/layout-typography-color.md` scopes dark to `/pos` only
 * and gives its reason as glare on a dimly-lit floor; a kitchen is the opposite
 * room — brightly lit, often daylight — and extending a documented scope is a
 * design-rule change, which would have to be made in that doc first.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Reads `GET /restaurant/kitchen-board`, writes only through `dispatchCommand`.
 */
export function KitchenBoardPage() {
  const t = useTranslate();
  const { locale } = useI18n();
  const errorMessage = useErrorMessage();
  const { data: session } = useSession();

  /*
   * Sign out, and the language switch beside it in the `Screen` header, are
   * deliberately NOT gated on `POS_USE` — the gate the exit-X below carries.
   *
   * The KITCHEN preset holds no `POS_USE`, so a cook had neither control: no
   * way to sign out of a tablet bolted to a wall that the whole kitchen shares,
   * and no way to switch to the language they read. Those are not back-office
   * capabilities; they belong to whoever is standing in front of the screen.
   */
  const { logout, isLoggingOut } = useLogout();
  const canView = hasPermission(session, PERMISSIONS.KITCHEN_VIEW);
  const canPrepare = hasPermission(session, PERMISSIONS.ITEM_PREPARE);
  const canServe = hasPermission(session, PERMISSIONS.ITEM_SERVE);
  // 86'ing (D50). The KITCHEN preset now carries it, but the control is gated
  // on the permission and never on the role: a store may hand it to anyone.
  const canToggleAvailability = hasPermission(
    session,
    PERMISSIONS.MENU_AVAILABILITY,
  );
  // Same gate as the till's own exit (`TableGridPage`): POS_USE is what marks
  // "this person has a back office to go back to". A cook holding only the
  // KITCHEN role deliberately gets no X — the board IS their whole app
  // (see the ManageShell note on the kitchen row), and an exit for them would
  // open an empty shell with nothing on it.
  const canLeaveBoard = canUseBackOffice(session);
  // The board wears the device's look like every other screen.
  useApplyDashboardTheme();
  /* The device's size (A6, T16): `touch`, `wall`, or the design's desk sizes.
     Carried as `data-kitchen-size` on the board's root, read only by CSS. */
  const kitchenSize = useKitchenSize();
  /* The board's root. `display: contents` so the shell keeps its own box (its
     `100svh` height and the realtime notice's subtraction) — the wrapper only
     carries the attribute every size rule in `_paper-kitchen.css` keys on. */
  /* LOUD AND HONEST (T12, F4; plan §4.11). The board's live-update state comes
     from the same provider `RealtimeNotice` reads; that notice yields on
     `/kitchen`, so the board's own band is the one warning. Optional: the
     screenshot harness renders the board with no provider at all. */
  const notifications = useOptionalRestaurantNotifications();
  const realtimeDown =
    notifications !== null &&
    (notifications.realtimeStatus === "degraded" ||
      notifications.realtimeStatus === "unconfigured" ||
      notifications.bootstrapStatus === "unavailable");
  /* «ابدأ الشاشة» — shown on each page load until tapped (audio unlock does
     not survive a reload). Not stored anywhere. */
  const [started, setStarted] = useState(false);
  const wakeLock = useScreenWakeLock();
  const [signOutOpen, setSignOutOpen] = useState(false);
  const [screenSheetOpen, setScreenSheetOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  /* «تحديث الشاشة» tapped while the DEVICE is offline: nothing can be read, so
     the band says so under its button until the browser's `online` event. */
  const [waitingForNetwork, setWaitingForNetwork] = useState(false);
  useEffect(() => {
    if (!waitingForNetwork) return;
    const back = () => setWaitingForNetwork(false);
    window.addEventListener("online", back);
    return () => window.removeEventListener("online", back);
  }, [waitingForNetwork]);
  /* Sign-out behind a hold (T16): the header's control opens the sheet; the
     sheet's 1.5 s hold is what signs out. `Screen` is unchanged. */
  const askToSignOut = () => setSignOutOpen(true);
  const frame = (screen: ReactNode, overlay: ReactNode = null, behindStart = false) => (
    <div
      data-ui="kitchen-board"
      data-testid="kitchen-board"
      data-kitchen-size={kitchenSize.size ?? undefined}
      className="contents"
    >
      {/* While the start overlay shows, the board behind it is `inert`: no
          focus and no taps reach it, but it stays mounted and keeps reading. */}
      <div className="contents" inert={behindStart}>
        {screen}
      </div>
      {overlay}
      <KitchenSignOutSheet
        isOpen={signOutOpen}
        onClose={() => setSignOutOpen(false)}
        onConfirm={() => {
          setSignOutOpen(false);
          logout();
        }}
        isLoggingOut={isLoggingOut}
      />
    </div>
  );

  /* LEAVE the board, for the back office — the same control, glyph and
     reasoning as the till's exit on `TableGridPage`: the board is this
     surface's root, so there is nothing to step "back" to and the X closes it
     rather than walking up a tree. Without it, an owner or cashier who
     followed the manage nav here was stranded on a chrome-less wall screen
     with the browser's back button as the only way home. */
  const exitToManage = canLeaveBoard ? (
    <Link
      to={`/${locale}/manage`}
      aria-label={t((m) => m.nav.manage)}
      title={t((m) => m.nav.manage)}
      className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[10px] border border-[color:var(--border)] text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--surface-alt)] hover:text-[color:var(--text)]"
      data-testid="kitchen-exit-to-manage"
    >
      <svg
        width="17"
        height="17"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M18 6 6 18M6 6l12 12" />
      </svg>
    </Link>
  ) : null;

  /* A phone's header has no room for it beside the count, the bell and the
     language switch — it was clipped out of reach at 375px — so there it
     leads the station row instead (2026-10-03 responsive pass). */
  const phone = usePhoneWidth();

  /* The way to the 86 screen. In the header rather than beside the plates:
     "the fish ran out" is a statement about the MENU, not about any one plate
     on the board, and a per-plate control would have to be repeated on every
     card of a dish that is fired four times. */
  const availabilityEntry = canToggleAvailability ? (
    <Link
      to={`/${locale}/kitchen/availability`}
      className="flex h-11 items-center rounded-xl border border-[color:var(--border-strong)] bg-[color:var(--surface)] px-4 text-sm font-extrabold text-[color:var(--text-primary)] transition-colors hover:bg-[color:var(--surface-alt)]"
      data-testid="kitchen-availability-entry"
    >
      {t((m) => m.kitchen.availabilityEntry)}
    </Link>
  ) : null;

  /* The board's data, clock, station choice and commands — shared with the
     PAPER floor's Kitchen tab, so the two surfaces are one board. */
  /* «حسب المحطة / حسب الطلب» (T5, T13; D170): per device, defaulting by
     PERMISSION — serve without prepare (the expediter) opens on orders. */
  const { view, setView } = useKitchenViewPreference({ canServe, canPrepare });
  const {
    board,
    plates,
    stationList,
    stationId,
    boardStationId,
    setStationId,
    canShowAllStations,
    stationGone,
    setStationGone,
    busyItemIds,
    now,
    runWithUndo,
    undo,
  } = useKitchenBoard({ enabled: canView, frozen: realtimeDown, everyStation: view === "order" });
  const workflow = boardWorkflow(board.data);

  const toast = usePrivateToast();
  const chimeSound = notifications?.chimeSound;
  const startSoundReminder = notifications?.startSoundReminder;
  const alertNewTicket = useCallback((key: string) => {
    const plate = board.data?.plates.find((candidate) => ticketChimeKey(ticketKeyOf(candidate)) === key);
    if (!plate || !session?.seller.storeId) return;
    chimeSound?.(key);
    toast.info(
      t((m) => m.notifications.kitchenTicketsTitle),
      t((m) => m.notifications.kitchenTicketsBodyFallback),
      {
        dedupeKey: `restaurant-kitchen:${session.seller.storeId}:${plate.invoiceId}`,
        durationMs: null,
        onDismiss: startSoundReminder?.(),
      },
    );
  }, [board.data, chimeSound, session, startSoundReminder, t, toast]);

  /* One alert per NEW ticket (T12), never on the first load or a station
     switch — `useNewTicketChime` keeps a baseline per station. */
  const ticketKeys = useMemo(
    () =>
      board.data ? [...new Set(board.data.plates.map((plate) => ticketKeyOf(plate)))] : null,
    [board.data],
  );
  useNewTicketChime({
    keys: ticketKeys,
    scope: boardStationId ?? "all",
    chime: alertNewTicket,
  });

  /* WHAT THE SCREEN DRAWS (D170). READY ages off after N minutes on THIS
     device (T2; «إظهار» pins one back) — per PLATE on the station view (the
     cook's), per ORDER on the order view: an order's READY plates stay while
     anything in it cooks, and a complete order leaves N after it completed —
     max(last ready, last void; P8) — whole (gate P4 #1). Voids are struck
     for five minutes (B5).
     Every plate a close collapsed while it cooked stays — «مدفوع» on a
     settled bill, «أُغلقت الفاتورة» on a closed one — until N after the close
     or «إخفاء» (T7; gate P4 #2), on both views. The filter «استلام وتوصيل»
     (T13) keeps takeaway and delivery only, for this page's life, and never
     re-sorts. */
  const ageOffMinutes = readyAgeOffMinutesOf(board.data?.readyAgeOffMinutes);
  const pinnedReady = useKitchenDeviceMarks(KITCHEN_PINNED_READY_KEY);
  const hiddenPaid = useKitchenDeviceMarks(KITCHEN_HIDDEN_PAID_KEY);
  const [offPremiseOnly, setOffPremiseOnly] = useState(false);
  const [recallOpen, setRecallOpen] = useState(false);
  const { live, agedOff } =
    view === "order"
      ? splitAgedOffByOrder(plates, ageOffMinutes, now, pinnedReady.ids, board.data?.recentlyVoided)
      : splitAgedOff(plates, ageOffMinutes, now, pinnedReady.ids);
  const voided = struckVoids(board.data?.recentlyVoided, now);
  const paid = visibleRetained(board.data?.retainedPaid, ageOffMinutes, now, hiddenPaid.ids);
  const keep = (plate: { channel: Parameters<typeof isOffPremise>[0] }) => !offPremiseOnly || isOffPremise(plate.channel);
  const shownPlates = live.filter(keep);
  const shownVoided = voided.filter(keep);
  const shownPaid = paid.filter(keep);
  /* «{n} مخفية»: the cards the filter hides — tickets on the lanes, orders
     on the order view. */
  const hiddenByFilter = offPremiseOnly
    ? new Set(
        [...live, ...voided, ...paid]
          .filter((plate) => !isOffPremise(plate.channel))
          .map((plate) => (view === "order" ? plate.invoiceId : ticketKeyOf(plate))),
      ).size
    : 0;
  const hasCards = shownPlates.length > 0 || shownVoided.length > 0 || shownPaid.length > 0;
  /* The header's count is what the screen DRAWS (gate P4 #7): the plates on
     the cards after age-off and the filter, retained ones included — never
     the plates that aged off into the drawer or the filter hides. */
  const drawnPlateCount = shownPlates.length + shownPaid.length;

  /* A NEW card is marked for six seconds (T12; `useNewTicketMark`), never on
     the first read of a station, a view or the filter. */
  const markSignature = board.data
    ? [...new Set(shownPlates.map((plate) => (view === "order" ? plate.invoiceId : ticketKeyOf(plate))))].join("\n")
    : null;
  const markKeys = useMemo(
    () => (markSignature === null ? null : markSignature.split("\n").filter(Boolean)),
    [markSignature],
  );
  const newKeys = useNewTicketMark(markKeys, `${view}:${boardStationId ?? "all"}:${offPremiseOnly ? "off" : "all"}`);

  /**
   * «تحديث الشاشة» — the band's one button, and the only recovery: a person's
   * tap, never a timer (F4). It re-attaches the listener (`retryBootstrap`) and
   * re-reads the board. With no Firebase the retry itself queues a catch-up
   * read of every active query two microtasks deep (`shared/realtime.ts`);
   * waiting those out first lets this read JOIN that one (`cancelRefetch:
   * false`) instead of racing it, so a tap is exactly one board request.
   */
  const refreshScreen = async () => {
    if (refreshing || waitingForNetwork) return;
    /* Offline, the read would only pause (TanStack waits for the network) and
       the button would sit disabled with no reason given (gate P5 #5). */
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      setWaitingForNetwork(true);
      return;
    }
    setRefreshing(true);
    try {
      notifications?.retryBootstrap();
      for (let tick = 0; tick < 4; tick += 1) await Promise.resolve();
      await board.refetch({ cancelRefetch: false });
    } finally {
      setRefreshing(false);
    }
  };
  const offlineBand = realtimeDown ? (
    <KitchenOfflineBand
      lastUpdatedAt={board.dataUpdatedAt}
      onRefresh={() => void refreshScreen()}
      refreshing={refreshing}
      waitingForNetwork={waitingForNetwork}
    />
  ) : null;

  /**
   * CAP THE LANES TO THE SCREEN (UX-round D48).
   * «UX-round Dn» = the 2026-09-17 UX round's own numbering (`.roundtable/2026-09-17-restaurant-ux/decisions.md`), not §11's.
   *
   * Measured rather than guessed with a `calc()`. What sits above the lanes is
   * not a constant: the station chip row wraps to two lines on a store with
   * five stations, and the "your station is gone" notice appears and
   * disappears. A hard-coded offset would be right on the store it was written
   * against and wrong on the next one — and being wrong here means either a
   * scrollbar on the page (the defect) or a lane shorter than it needs to be.
   *
   * Only at `lg`, only when there is room for at least one whole card, and
   * `null` otherwise — in which case the board falls back to exactly what it
   * did before, which is the correct behaviour on a stacked phone layout.
   */
  const lanesRef = useRef<HTMLDivElement | null>(null);
  const [lanesHeight, setLanesHeight] = useState<number | null>(null);
  useLayoutEffect(() => {
    const node = lanesRef.current;
    if (!node || typeof window === "undefined") return;
    const measure = () => {
      if (!window.matchMedia?.(LANES_SIDE_BY_SIDE).matches) {
        // The value set here IS a measurement of the DOM, which no render can
        // derive: it depends on where the browser laid this element out after
        // the chip row wrapped.
        setLanesHeight(null);
        return;
      }
      const top = node.getBoundingClientRect().top;
      const main = node.closest("main");
      const padding = main
        ? Number.parseFloat(window.getComputedStyle(main).paddingBottom)
        : Number.NaN;
      const gutter = Number.isFinite(padding)
        ? padding
        : BOARD_BOTTOM_GUTTER_PX;
      const available = window.innerHeight - top - gutter;
      setLanesHeight(available >= MIN_LANE_HEIGHT_PX ? available : null);
    };
    measure();
    window.addEventListener("resize", measure);
    /* The chip row reflowing does not fire `resize`. Observing the lanes' own
       parent catches it — and the stub in `test-support/setup.ts` makes this a
       no-op under jsdom, where nothing is laid out anyway. */
    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(measure);
    if (observer && node.parentElement) observer.observe(node.parentElement);
    return () => {
      window.removeEventListener("resize", measure);
      observer?.disconnect();
    };
    // `plates.length` and `stationGone` both change what sits above the lanes,
    // and the look changes `main`'s padding.
    // The disconnected band appearing or leaving moves the lanes' top too.
  }, [plates.length, stationGone, stationList.length, realtimeDown, view, hasCards, offPremiseOnly]);

  // Server-side `@RequirePermission` is the real gate; this is so a cashier who
  // lands on the URL gets an explanation instead of a board full of 403s.
  if (session && !canView) {
    return frame(
      // The exit stays on the denied screen too — this is exactly where a
      // cashier who landed on the URL by hand needs the way home.
      <Screen
        title={t((m) => m.kitchen.title)}
        leading={exitToManage}
        onLogout={askToSignOut}
        isLoggingOut={isLoggingOut}
      >
        {offlineBand}
        <EmptyState
          title={t((m) => m.kitchen.deniedTitle)}
          description={t((m) => m.kitchen.deniedBody)}
        />
      </Screen>
    );
  }

  /*
   * The store does not run a kitchen display (D18). Gated on the PAYLOAD, not
   * on a settings fetch a cook could never make — and only once data arrived,
   * so the gate can't flash during the first load. Without this the board was
   * reachable in every mode: in "neither" mode its lanes were structurally
   * empty (plates auto-start at fire), and in print mode a cook tapping
   * "start" here would move food the chit printer is supposed to move —
   * bypassing the one actuator staff cannot forget.
   */
  if (board.data && !board.data.kitchenDisplayEnabled) {
    const canOpenSettings = hasPermission(session, PERMISSIONS.SETTINGS_MANAGE);
    return frame(
      <Screen
        title={t((m) => m.kitchen.title)}
        leading={exitToManage}
        onLogout={askToSignOut}
        isLoggingOut={isLoggingOut}
        // The 86 screen is still reachable with the display off: a store that
        // runs on printed chits still runs out of fish, and this is the only
        // surface a cook has.
        action={availabilityEntry}
      >
        {offlineBand}
        <EmptyState
          title={t((m) => m.kitchen.displayOffTitle)}
          description={
            canOpenSettings
              ? t((m) => m.kitchen.displayOffBodyOwner)
              : t((m) => m.kitchen.displayOffBody)
          }
          // The remedy, for whoever may apply it: «طريقة عمل المطبخ» lives on
          // Settings → Kitchen (D173 — it pointed at
          // `/settings/general`, which holds no kitchen setting). Everyone else
          // gets the explanation alone — a button onto a 403 teaches staff to
          // distrust the app.
          actionLabel={
            canOpenSettings
              ? t((m) => m.kitchen.displayOffSettingsAction)
              : undefined
          }
          actionHref={
            canOpenSettings ? `/${locale}/manage/settings/kitchen` : undefined
          }
        />
      </Screen>
    );
  }

  const activeStation = stationList.find((one) => one.id === stationId);
  const stationLabel = (station: KitchenStation) =>
    locale === "en" && station.nameEn ? station.nameEn : station.nameAr;

  const plateCount = (
    <span data-testid="kitchen-plate-count" className="text-sm font-bold text-[color:var(--text-secondary)]">
      {t(
        // B117/A2 — the DUAL. `plural.ts` holds the rule; this call site
        // was still spelling one form for every number.
        countedKey(drawnPlateCount, {
          one: (m) => m.kitchen.plateCountOne,
          two: (m) => m.kitchen.plateCountTwo,
          few: (m) => m.kitchen.plateCountFew,
          many: (m) => m.kitchen.plateCount,
        }),
        { count: drawnPlateCount },
      )}
    </span>
  );

  /* «المُنجز مؤخرًا» (T3; D170): the recall drawer, for whoever may cook or
     serve — the last 15 minutes' hand-overs and the READY plates that aged
     off this screen. */
  const recallButton =
    canServe || canPrepare ? (
      <Button
        variant="outline"
        size="sm"
        data-testid="kitchen-recall-open"
        data-count={agedOff.length + (board.data?.recentServed?.length ?? 0)}
        onClick={() => setRecallOpen(true)}
      >
        {t((m) => m.kitchen.recallOpen)}
      </Button>
    ) : null;

  /* «الشاشة» — this device's size and sound (plan §4.12). */
  const screenButton = (
    <Button
      variant="outline"
      size="sm"
      data-testid="kitchen-screen-open"
      onClick={() => setScreenSheetOpen(true)}
    >
      {t((m) => m.kitchen.screenOpen)}
    </Button>
  );
  const startOverlay = started ? null : (
    <KitchenStartOverlay
      sizeChoice={kitchenSize.choice}
      onSizeChange={kitchenSize.setChoice}
      onStart={() => {
        // The tap IS the gesture both APIs want: audio unlock with a sample
        // tone, then the wake lock.
        void notifications?.enableSound();
        wakeLock.acquire();
        setStarted(true);
      }}
      onSilent={() => {
        // Any tap on the page unlocks audio while the device's sound is on
        // (`useNotificationSound`), so "without sound" must say so to the
        // device — the screen sheet turns it back on.
        notifications?.muteSound();
        wakeLock.acquire();
        setStarted(true);
      }}
    />
  );

  return frame(
    <Screen
      title={
        activeStation
          ? t((m) => m.kitchen.titleAtStation, {
              station: stationLabel(activeStation),
            })
          : t((m) => m.kitchen.title)
      }
      leading={exitToManage}
      onLogout={askToSignOut}
      isLoggingOut={isLoggingOut}
      action={
        phone ? undefined : (
          <span className="flex items-center gap-2.5">
            {plateCount}
            {availabilityEntry}
            {recallButton}
            {screenButton}
          </span>
        )
      }
    >
      {offlineBand}
      {/* The station picker.
          Buttons at 48px rather than a select or the shared `Segmented`
          (34px tall): this is the one control on the screen a cook touches with
          the back of a knuckle, and the two shared options are both built for a
          desk. It is a FILTER over one board, not navigation, so it is a chip
          row — the tabs-vs-chips rule in the interaction contract. */}
      {phone ? (
        <div className="no-print mb-3 flex items-center gap-2.5">
          {plateCount}
          {availabilityEntry}
          {recallButton}
          {screenButton}
        </div>
      ) : null}
      {/* The board's tools (D170): the view, the station (lanes only — the
          order view reads every station) and the takeaway/delivery filter.
          Chips at 48px, not the shared `Segmented`: a cook taps these with a
          knuckle (the tabs-vs-chips rule). */}
      <div data-ui="kitchen-board-tools" className="no-print mb-4 flex flex-wrap items-center gap-2">
        {hasBoard(workflow) ? (
          <div
            role="group"
            aria-label={t((m) => m.kitchen.viewSwitchLabel)}
            data-ui="kitchen-view-switch"
            className="flex flex-wrap gap-2"
          >
            {(["station", "order"] as const satisfies readonly KitchenView[]).map((one) => (
              <button
                key={one}
                type="button"
                aria-pressed={view === one}
                data-ui="station-chip"
                data-active={view === one ? "" : undefined}
                data-testid={`kitchen-view-${one}`}
                onClick={() => setView(one)}
                className="min-h-12 rounded-xl border px-5 text-base font-extrabold transition-colors"
              >
                {one === "station" ? t((m) => m.kitchen.viewByStation) : t((m) => m.kitchen.viewByOrder)}
              </button>
            ))}
          </div>
        ) : null}
        {view === "station" ? (
          <div
            role="group"
            aria-label={t((m) => m.kitchen.stationPickerLabel)}
            data-ui="kitchen-stations"
            className="flex flex-wrap gap-2"
          >
            {[
              // D128 — a role kept to one station has no "all" to offer.
              ...(canShowAllStations ? [null] : []),
              ...stationList.filter(
                (one) =>
                  // Paused stations lose their chip — EXCEPT while plates fired
                  // to them before the pause are still in flight. Hiding those
                  // would strand real food where only the "all" view can see it,
                  // and a cook filtered to their own section never looks there.
                  one.isActive ||
                  plates.some((plate) => plate.stationId === one.id),
              ),
            ].map((station) => {
              const id = station?.id ?? null;
              const isActive = stationId === id;
              const paused = station ? !station.isActive : false;
              return (
                <button
                  key={id ?? "all"}
                  type="button"
                  aria-pressed={isActive}
                  onClick={() => {
                    setStationId(id);
                    // The notice is about the choice being GONE; a new choice
                    // answers it, so it goes rather than lingering as noise.
                    setStationGone(false);
                  }}
                  data-testid={`kitchen-station-${id ?? "all"}`}
                  data-ui="station-chip"
                  data-active={isActive ? "" : undefined}
                  className={[
                    "min-h-12 rounded-xl border px-5 text-base font-extrabold transition-colors",
                    isActive
                      ? "border-[color:var(--primary-500)] bg-[color:var(--primary-500)] text-[color:var(--on-accent,#fff)]"
                      : "border-[color:var(--border-strong)] bg-[color:var(--surface)] text-[color:var(--text-primary)] hover:bg-[color:var(--surface-alt)]",
                  ].join(" ")}
                >
                  {station
                    ? paused
                      ? `${stationLabel(station)} ${t((m) => m.manage.stationPausedSuffix)}`
                      : stationLabel(station)
                    : t((m) => m.kitchen.allStations)}
                </button>
              );
            })}
          </div>
        ) : null}
        <span data-ui="kitchen-filter" className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            aria-pressed={offPremiseOnly}
            data-ui="station-chip"
            data-active={offPremiseOnly ? "" : undefined}
            data-testid="kitchen-filter-offpremise"
            onClick={() => setOffPremiseOnly((on) => !on)}
            className="min-h-12 rounded-xl border px-5 text-base font-extrabold transition-colors"
          >
            {t((m) => m.kitchen.channelFilter)}
          </button>
          {offPremiseOnly && hiddenByFilter > 0 ? (
            <span data-ui="kitchen-filter-hidden" data-testid="kitchen-filter-hidden" role="status" className="tabular-nums">
              {t((m) => m.kitchen.channelFilterHidden, { count: hiddenByFilter })}
            </span>
          ) : null}
        </span>
      </div>

      {stationGone ? (
        /* One line, above the lanes and below the picker it is about. `status`
           rather than `alert`: nothing is broken and no food is at risk — the
           board is simply showing more than the device asked for. */
        <p
          role="status"
          data-testid="kitchen-station-gone"
          className="mb-4 rounded-xl border border-[color:var(--warning)] px-4 py-3 text-base font-semibold text-[color:var(--text-primary)]"
        >
          {t((m) => m.kitchen.stationGoneNotice)}
        </p>
      ) : null}

      {board.isLoading ? (
        <div
          aria-busy="true"
          aria-live="polite"
          aria-label={t((m) => m.common.loading)}
        >
          <LoadingSkeleton rows={6} />
        </div>
      ) : null}

      {board.error ? (
        <ErrorState
          message={errorMessage(board.error)}
          retryLabel={t((m) => m.common.retry)}
          /* The query on screen, whatever its key: in the order view that is
             every station's board, not the remembered station's. */
          onRetry={() => void board.refetch()}
        />
      ) : null}

      {/* «المطلوب الآن» (T13): on one station whose strip is on — never on
          «كل المحطات» and never on the order view. */}
      {view === "station" && activeStation && activeStation.batchStripEnabled !== false ? (
        <KitchenBatchStrip plates={live} />
      ) : null}

      {board.data && view === "station" && !hasCards ? (
        <EmptyState
          title={t((m) => m.kitchen.emptyTitle)}
          description={t((m) => m.kitchen.emptyBody)}
        />
      ) : null}

      {board.data && view === "order" ? (
        <KitchenOrderView
          workflow={workflow}
          plates={shownPlates}
          paid={shownPaid}
          onHidePaid={hiddenPaid.add}
          stations={stationList}
          now={now}
          busyItemIds={busyItemIds}
          canServe={canServe}
          newKeys={newKeys}
          onRun={runWithUndo}
        />
      ) : null}

      {view === "station" && hasCards ? (
        /* The lanes by WORKFLOW (D170), shared with the floor's Kitchen tab.
           `lanesHeight` (UX-round D48) caps the row to what is left of the
           viewport and each lane scrolls inside it; PAPER keeps the cap too
           (founder, 2026-10-03). */
        <KitchenLanes
          lanesRef={lanesRef}
          testId="kitchen-lanes"
          workflow={workflow}
          plates={shownPlates}
          voided={shownVoided}
          paid={shownPaid}
          now={now}
          busyItemIds={busyItemIds}
          canPrepare={canPrepare}
          canServe={canServe}
          isCapped={lanesHeight !== null}
          style={lanesHeight === null ? undefined : { height: lanesHeight }}
          newKeys={newKeys}
          onRun={runWithUndo}
          onHidePaid={hiddenPaid.add}
        />
      ) : null}
    </Screen>,
    <>
      {startOverlay}
      <KitchenRecallDrawer
        isOpen={recallOpen}
        onClose={() => setRecallOpen(false)}
        recentServed={board.data?.recentServed ?? []}
        agedOff={agedOff}
        agedBy={view === "order" ? "order" : "ticket"}
        now={now}
        canServe={canServe}
        busyItemIds={busyItemIds}
        onUndoServed={(targets) => void undo(targets, "undoServed")}
        onServe={(targets) => void runWithUndo(targets, "markServed")}
        onShow={(ids) => pinnedReady.add(ids)}
      />
      <KitchenScreenSheet
        isOpen={screenSheetOpen}
        onClose={() => setScreenSheetOpen(false)}
        sizeChoice={kitchenSize.choice}
        onSizeChange={kitchenSize.setChoice}
        soundStatus={notifications?.soundStatus ?? null}
        onEnableSound={() => void notifications?.enableSound()}
        onMuteSound={() => notifications?.muteSound()}
      />
    </>,
    startOverlay !== null,
  );
}
